import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { abacGuard } from '@saraa/permissions/abacMiddleware.js';
import { TemplateFields } from '@saraa/permissions/policies/index.js';
import { PAGE_CHECK } from '@saraa/permissions/policyHelper.js';
import { TemplateField } from '../entities/TemplateField.js';
import { loadTemplateFieldTarget } from '../server/abacTargets.js';
import {
  resolveGetTemplateFieldById,
  resolveSubscribeTemplateFields,
} from '../server/resolvers/resolveReadTemplateFields.js';
import {
  resolveCreateTemplateField,
  resolveDeleteTemplateField,
  resolveSetTemplateFieldArchived,
  resolveUpdateTemplateField,
} from '../server/resolvers/resolveWriteTemplateField.js';

/**
 * Every way a template field can be read or written. The generic entity
 * mutations are closed for this class, so these are it.
 *
 * Each endpoint states the permission it needs. For everything that addresses an
 * existing field, the guard loads that field and decides against *its*
 * organization; `create` is the one case where the organization comes from the
 * request, which is exactly the claim `isOrgAdminOf` is there to verify.
 */

const createTemplateField = registerMutation({
  operationName: 'createTemplateField',
  type: () => ID,
  params: [
    // Explicit rather than read off the entity: this is the value the guard
    // decides against, so it must be the same one the resolver writes.
    { name: 'organizationId', type: () => ID },
    { name: 'templateField', type: () => TemplateField },
  ],
  middlewares: [
    abacGuard(TemplateFields, 'create', (args: { organizationId: string }) => ({
      organizationId: args.organizationId,
    })),
  ],
  resolve: resolveCreateTemplateField,
});

const updateTemplateField = registerMutation({
  operationName: 'updateTemplateField',
  type: () => Boolean,
  params: [{ name: 'templateField', type: () => TemplateField }],
  middlewares: [
    abacGuard(TemplateFields, 'modify', (args: { templateField: TemplateField }) =>
      loadTemplateFieldTarget(args.templateField?._id),
    ),
  ],
  resolve: resolveUpdateTemplateField,
});

const deleteTemplateField = registerMutation({
  operationName: 'deleteTemplateField',
  type: () => Boolean,
  params: [{ name: 'templateFieldId', type: () => ID }],
  middlewares: [
    abacGuard(TemplateFields, 'delete', (args: { templateFieldId: string }) =>
      loadTemplateFieldTarget(args.templateFieldId),
    ),
  ],
  resolve: resolveDeleteTemplateField,
});

const setTemplateFieldArchived = registerMutation({
  operationName: 'setTemplateFieldArchived',
  type: () => Boolean,
  params: [
    { name: 'templateFieldId', type: () => ID },
    { name: 'archived', type: () => Boolean },
  ],
  // Archiving takes a field out of use, so it answers to the same permission as
  // deleting it.
  middlewares: [
    abacGuard(TemplateFields, 'delete', (args: { templateFieldId: string }) =>
      loadTemplateFieldTarget(args.templateFieldId),
    ),
  ],
  resolve: resolveSetTemplateFieldArchived,
});

const subscribeTemplateFields = registerSubscription({
  operationName: 'subscribeTemplateFields',
  type: () => [TemplateField],
  // `(skip, limit, filter)` is the shape the data-view calls a list operation with.
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
  middlewares: [abacGuard(TemplateFields, 'view', () => PAGE_CHECK)],
  resolve: resolveSubscribeTemplateFields,
});

const getTemplateFieldById = registerQuery({
  operationName: 'getTemplateFieldById',
  type: () => TemplateField,
  params: [{ name: 'templateFieldId', type: () => ID }],
  middlewares: [
    abacGuard(TemplateFields, 'view', (args: { templateFieldId: string }) =>
      loadTemplateFieldTarget(args.templateFieldId),
    ),
  ],
  resolve: resolveGetTemplateFieldById,
});

export const TemplateFieldAPI = {
  create: createTemplateField,
  update: updateTemplateField,
  delete: deleteTemplateField,
  setArchived: setTemplateFieldArchived,
  subscribe: subscribeTemplateFields,
  getById: getTemplateFieldById,
};
